import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ArrowLeft, Plus, Edit, Trash2, Users, UserCog, Camera, Building } from 'lucide-react';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import { useAuth } from '../AuthContext';

// Convert File → base64 data URL for photo capture
const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const dateIN = (s) => (s ? new Date(s).toLocaleDateString('en-IN') : '—');

const emptyInmate = {
  name: '', dob: '', gender: 'Female', guardian_name: '', guardian_contact: '',
  address: '', admission_date: '', aadhaar: '', medical_notes: '',
  height_cm: '', weight_kg: '', status: 'active', remarks: '',
};

const emptyStaff = {
  name: '', designation: '', contact: '', doj: '', dob: '', address: '',
  salary: '', bank_name: '', bank_account: '', bank_ifsc: '', aadhaar: '',
  status: 'active', remarks: '',
};

const CentreDetail = () => {
  const { centreId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canRead = user?.role === 'Admin' || user?.role === 'CentreManager' || user?.role === 'HR' || user?.role === 'Account';
  const canManage = user?.role === 'Admin' || user?.role === 'CentreManager';

  const [centre, setCentre] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('inmates');

  // Inmates state
  const [inmates, setInmates] = useState([]);
  const [inmateDlg, setInmateDlg] = useState({ open: false, editing: null });
  const [inmateForm, setInmateForm] = useState(emptyInmate);
  const [inmatePhoto, setInmatePhoto] = useState(null);
  const [delInmate, setDelInmate] = useState({ open: false, item: null });

  // Staff state
  const [staff, setStaff] = useState([]);
  const [staffDlg, setStaffDlg] = useState({ open: false, editing: null });
  const [staffForm, setStaffForm] = useState(emptyStaff);
  const [staffPhoto, setStaffPhoto] = useState(null);
  const [delStaff, setDelStaff] = useState({ open: false, item: null });

  const fetchAll = useCallback(async () => {
    if (!canRead) { setLoading(false); return; }
    setLoading(true);
    try {
      const [cRes, iRes, sRes] = await Promise.all([
        api.get(`/centres/${centreId}`),
        api.get('/inmates', { params: { centre_id: centreId } }),
        api.get('/centre-staff', { params: { centre_id: centreId } }),
      ]);
      setCentre(cRes.data);
      setInmates(iRes.data || []);
      setStaff(sRes.data || []);
    } catch {
      toast.error('Failed to load centre details');
    } finally {
      setLoading(false);
    }
  }, [centreId, canRead]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (!canRead) {
    return (
      <div className="p-8 text-center" data-testid="centre-access-denied">
        <Building className="w-12 h-12 mx-auto text-slate-300 mb-3" />
        <h2 className="text-xl font-bold text-slate-800 mb-1">Access denied</h2>
        <p className="text-slate-500 mb-4">Inmate and centre-staff records are restricted. Contact your administrator if you need access.</p>
        <Link className="text-emerald-700 underline" to="/">Back to dashboard</Link>
      </div>
    );
  }

  // ---------- Inmate handlers ----------
  const openInmateAdd = () => { setInmateForm(emptyInmate); setInmatePhoto(null); setInmateDlg({ open: true, editing: null }); };
  const openInmateEdit = (row) => {
    setInmateForm({
      name: row.name || '', dob: row.dob || '', gender: row.gender || 'Female',
      guardian_name: row.guardian_name || '', guardian_contact: row.guardian_contact || '',
      address: row.address || '', admission_date: row.admission_date || '',
      aadhaar: row.aadhaar || '', medical_notes: row.medical_notes || '',
      height_cm: row.height_cm ?? '', weight_kg: row.weight_kg ?? '',
      status: row.status || 'active', remarks: row.remarks || '',
    });
    setInmatePhoto(null);
    setInmateDlg({ open: true, editing: row });
  };

  const submitInmate = async (e) => {
    e.preventDefault();
    if (!inmateForm.name.trim()) { toast.error('Name is required'); return; }
    try {
      const payload = {
        centre_id: centreId,
        ...inmateForm,
        height_cm: inmateForm.height_cm !== '' ? Number(inmateForm.height_cm) : null,
        weight_kg: inmateForm.weight_kg !== '' ? Number(inmateForm.weight_kg) : null,
      };
      const fd = new FormData();
      fd.append('data', JSON.stringify(payload));
      if (inmatePhoto) fd.append('photo', inmatePhoto);
      if (inmateDlg.editing) {
        await api.put(`/inmates/${inmateDlg.editing.id}`, fd);
        toast.success('Inmate updated');
      } else {
        await api.post('/inmates', fd);
        toast.success('Inmate added');
      }
      setInmateDlg({ open: false, editing: null });
      setInmateForm(emptyInmate);
      setInmatePhoto(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const doDeleteInmate = async () => {
    try {
      await api.delete(`/inmates/${delInmate.item.id}`);
      toast.success('Inmate deleted');
      setDelInmate({ open: false, item: null });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  // ---------- Staff handlers ----------
  const openStaffAdd = () => { setStaffForm(emptyStaff); setStaffPhoto(null); setStaffDlg({ open: true, editing: null }); };
  const openStaffEdit = (row) => {
    setStaffForm({
      name: row.name || '', designation: row.designation || '', contact: row.contact || '',
      doj: row.doj || '', dob: row.dob || '', address: row.address || '',
      salary: row.salary ?? '', bank_name: row.bank_name || '',
      bank_account: row.bank_account || '', bank_ifsc: row.bank_ifsc || '',
      aadhaar: row.aadhaar || '', status: row.status || 'active', remarks: row.remarks || '',
    });
    setStaffPhoto(null);
    setStaffDlg({ open: true, editing: row });
  };

  const submitStaff = async (e) => {
    e.preventDefault();
    if (!staffForm.name.trim()) { toast.error('Name is required'); return; }
    try {
      const payload = {
        centre_id: centreId,
        ...staffForm,
        salary: staffForm.salary !== '' ? Number(staffForm.salary) : null,
      };
      const fd = new FormData();
      fd.append('data', JSON.stringify(payload));
      if (staffPhoto) fd.append('photo', staffPhoto);
      if (staffDlg.editing) {
        await api.put(`/centre-staff/${staffDlg.editing.id}`, fd);
        toast.success('Staff updated');
      } else {
        await api.post('/centre-staff', fd);
        toast.success('Staff added');
      }
      setStaffDlg({ open: false, editing: null });
      setStaffForm(emptyStaff);
      setStaffPhoto(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const doDeleteStaff = async () => {
    try {
      await api.delete(`/centre-staff/${delStaff.item.id}`);
      toast.success('Staff deleted');
      setDelStaff({ open: false, item: null });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  // Photo upload input handler (both dialogs share the pattern)
  const handlePhotoChange = async (e, setter) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Photo must be under 5 MB'); return; }
    try {
      const b64 = await fileToBase64(file);
      setter(b64);
    } catch {
      toast.error('Could not read photo');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-700"></div></div>;
  }
  if (!centre) {
    return <div className="p-8 text-center text-slate-500">Centre not found. <Link className="text-emerald-700 underline" to="/projects">Go to Projects</Link></div>;
  }

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="centre-detail-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="flex items-start gap-3">
          <button onClick={() => navigate('/projects')} className="text-slate-500 hover:text-slate-800 mt-1" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <Building className="w-6 h-6 text-emerald-700" />
              <h1 className="text-2xl font-bold text-slate-900" data-testid="centre-name">{centre.name}</h1>
              {centre.code && <span className="text-sm font-mono text-slate-500">({centre.code})</span>}
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${centre.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}`}>{centre.status}</span>
            </div>
            <p className="text-sm text-slate-600 mt-1">
              {centre.address || 'Address not set'} {centre.capacity ? `• Capacity: ${centre.capacity}` : ''}
            </p>
            {centre.contact_person && <p className="text-xs text-slate-500 mt-0.5">Contact: {centre.contact_person}{centre.contact_phone ? ` — ${centre.contact_phone}` : ''}</p>}
          </div>
        </div>
        <div className="flex gap-4 text-center">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2">
            <p className="text-[10px] uppercase font-semibold text-emerald-700">Inmates</p>
            <p className="text-xl font-bold text-emerald-800" data-testid="inmate-count">{inmates.length}</p>
          </div>
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2">
            <p className="text-[10px] uppercase font-semibold text-indigo-700">Staff</p>
            <p className="text-xl font-bold text-indigo-800" data-testid="staff-count">{staff.length}</p>
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList data-testid="centre-tabs">
          <TabsTrigger value="inmates" data-testid="tab-inmates" className="flex items-center gap-1"><Users className="w-4 h-4" /> Inmates</TabsTrigger>
          <TabsTrigger value="staff" data-testid="tab-staff" className="flex items-center gap-1"><UserCog className="w-4 h-4" /> Staff</TabsTrigger>
        </TabsList>

        {/* --- Inmates Tab --- */}
        <TabsContent value="inmates" className="mt-4">
          <div className="flex justify-between items-center mb-3">
            <p className="text-sm text-slate-600">{inmates.length} inmate{inmates.length === 1 ? '' : 's'} on record</p>
            {canManage && (
              <Button onClick={openInmateAdd} className="bg-emerald-700 hover:bg-emerald-800 text-white" size="sm" data-testid="add-inmate-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Inmate
              </Button>
            )}
          </div>

          {inmates.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-lg border border-dashed border-slate-300">
              <Users className="w-10 h-10 mx-auto text-slate-300 mb-2" />
              <p className="text-slate-500 text-sm">No inmates registered yet.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto shadow-sm">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">#</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Photo</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Enrolment</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Gender</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">DOB</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Guardian</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Admitted</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">H/W</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                    {canManage && <th className="px-3 py-2 text-xs font-semibold text-slate-600 uppercase">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {inmates.map((row, i) => (
                    <tr key={row.id} className="hover:bg-emerald-50/40" data-testid={`inmate-row-${row.id}`}>
                      <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                      <td className="px-3 py-2">
                        {row.photo_url ? <img src={row.photo_url} alt={row.name} className="w-10 h-10 rounded-full object-cover border border-slate-200" loading="lazy" /> : <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 text-xs">—</div>}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-700">{row.enrollment_no}</td>
                      <td className="px-3 py-2 font-medium text-slate-900">{row.name}</td>
                      <td className="px-3 py-2 text-slate-600">{row.gender || '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{dateIN(row.dob)}</td>
                      <td className="px-3 py-2 text-slate-600">
                        {row.guardian_name || '—'}
                        {row.guardian_contact && <div className="text-xs text-slate-500">{row.guardian_contact}</div>}
                      </td>
                      <td className="px-3 py-2 text-slate-600">{dateIN(row.admission_date)}</td>
                      <td className="px-3 py-2 text-slate-600 text-xs">{row.height_cm ? `${row.height_cm}cm` : '—'} / {row.weight_kg ? `${row.weight_kg}kg` : '—'}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${row.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}`}>{row.status}</span>
                      </td>
                      {canManage && (
                        <td className="px-3 py-2">
                          <div className="flex gap-1 justify-end">
                            <Button variant="ghost" size="sm" onClick={() => openInmateEdit(row)} className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50" data-testid={`edit-inmate-${row.id}`}>
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setDelInmate({ open: true, item: row })} className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" data-testid={`delete-inmate-${row.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* --- Staff Tab --- */}
        <TabsContent value="staff" className="mt-4">
          <div className="flex justify-between items-center mb-3">
            <p className="text-sm text-slate-600">{staff.length} staff member{staff.length === 1 ? '' : 's'} on record</p>
            {canManage && (
              <Button onClick={openStaffAdd} className="bg-indigo-700 hover:bg-indigo-800 text-white" size="sm" data-testid="add-staff-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Staff
              </Button>
            )}
          </div>

          {staff.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-lg border border-dashed border-slate-300">
              <UserCog className="w-10 h-10 mx-auto text-slate-300 mb-2" />
              <p className="text-slate-500 text-sm">No staff registered yet.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto shadow-sm">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">#</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Photo</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Name</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Designation</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Contact</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">DOJ</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600 uppercase">Salary</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Bank</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                    {canManage && <th className="px-3 py-2 text-xs font-semibold text-slate-600 uppercase">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {staff.map((row, i) => (
                    <tr key={row.id} className="hover:bg-indigo-50/40" data-testid={`staff-row-${row.id}`}>
                      <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                      <td className="px-3 py-2">
                        {row.photo_url ? <img src={row.photo_url} alt={row.name} className="w-10 h-10 rounded-full object-cover border border-slate-200" loading="lazy" /> : <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 text-xs">—</div>}
                      </td>
                      <td className="px-3 py-2 font-medium text-slate-900">{row.name}</td>
                      <td className="px-3 py-2 text-slate-600">{row.designation || '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{row.contact || '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{dateIN(row.doj)}</td>
                      <td className="px-3 py-2 text-right text-slate-700 font-medium">{row.salary ? `Rs.${Number(row.salary).toLocaleString('en-IN')}` : '—'}</td>
                      <td className="px-3 py-2 text-slate-600 text-xs">
                        {row.bank_name ? <div>{row.bank_name}</div> : '—'}
                        {row.bank_account && <div className="text-slate-500">A/c: {row.bank_account}</div>}
                        {row.bank_ifsc && <div className="text-slate-500">IFSC: {row.bank_ifsc}</div>}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${row.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}`}>{row.status}</span>
                      </td>
                      {canManage && (
                        <td className="px-3 py-2">
                          <div className="flex gap-1 justify-end">
                            <Button variant="ghost" size="sm" onClick={() => openStaffEdit(row)} className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50" data-testid={`edit-staff-${row.id}`}>
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setDelStaff({ open: true, item: row })} className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" data-testid={`delete-staff-${row.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ============ Inmate Dialog ============ */}
      <Dialog open={inmateDlg.open} onOpenChange={(v) => setInmateDlg({ open: v, editing: v ? inmateDlg.editing : null })}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="inmate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-emerald-700" /> {inmateDlg.editing ? 'Edit Inmate' : 'Add Inmate'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitInmate} className="space-y-3">
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                {inmatePhoto ? <img src={inmatePhoto} alt="preview" className="w-full h-full object-cover" /> :
                  inmateDlg.editing?.photo_url ? <img src={inmateDlg.editing.photo_url} alt="current" className="w-full h-full object-cover" /> :
                  <Camera className="w-8 h-8 text-slate-300" />}
              </div>
              <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-100 hover:bg-slate-200 text-sm">
                <Camera className="w-4 h-4" /> Upload Photo
                <input type="file" accept="image/*" onChange={(e) => handlePhotoChange(e, setInmatePhoto)} className="hidden" data-testid="inmate-photo-input" />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Name *</Label><Input value={inmateForm.name} onChange={(e) => setInmateForm({ ...inmateForm, name: e.target.value })} required data-testid="inmate-name" /></div>
              <div><Label>Gender</Label>
                <Select value={inmateForm.gender} onValueChange={(v) => setInmateForm({ ...inmateForm, gender: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Date of Birth</Label><Input type="date" value={inmateForm.dob} onChange={(e) => setInmateForm({ ...inmateForm, dob: e.target.value })} /></div>
              <div><Label>Admission Date</Label><Input type="date" value={inmateForm.admission_date} onChange={(e) => setInmateForm({ ...inmateForm, admission_date: e.target.value })} data-testid="inmate-admission" /></div>
              <div><Label>Guardian Name</Label><Input value={inmateForm.guardian_name} onChange={(e) => setInmateForm({ ...inmateForm, guardian_name: e.target.value })} /></div>
              <div><Label>Guardian Contact</Label><Input value={inmateForm.guardian_contact} onChange={(e) => setInmateForm({ ...inmateForm, guardian_contact: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Address</Label><Input value={inmateForm.address} onChange={(e) => setInmateForm({ ...inmateForm, address: e.target.value })} /></div>
              <div><Label>Aadhaar No</Label><Input value={inmateForm.aadhaar} onChange={(e) => setInmateForm({ ...inmateForm, aadhaar: e.target.value })} /></div>
              <div><Label>Status</Label>
                <Select value={inmateForm.status} onValueChange={(v) => setInmateForm({ ...inmateForm, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="discharged">Discharged</SelectItem>
                    <SelectItem value="transferred">Transferred</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Height (cm)</Label><Input type="number" step="0.1" value={inmateForm.height_cm} onChange={(e) => setInmateForm({ ...inmateForm, height_cm: e.target.value })} /></div>
              <div><Label>Weight (kg)</Label><Input type="number" step="0.1" value={inmateForm.weight_kg} onChange={(e) => setInmateForm({ ...inmateForm, weight_kg: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Medical Notes</Label><Textarea rows={2} value={inmateForm.medical_notes} onChange={(e) => setInmateForm({ ...inmateForm, medical_notes: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Remarks</Label><Textarea rows={2} value={inmateForm.remarks} onChange={(e) => setInmateForm({ ...inmateForm, remarks: e.target.value })} /></div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button type="button" variant="outline" onClick={() => setInmateDlg({ open: false, editing: null })}>Cancel</Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="save-inmate-btn">
                {inmateDlg.editing ? 'Update Inmate' : 'Add Inmate'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ============ Staff Dialog ============ */}
      <Dialog open={staffDlg.open} onOpenChange={(v) => setStaffDlg({ open: v, editing: v ? staffDlg.editing : null })}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="staff-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserCog className="w-5 h-5 text-indigo-700" /> {staffDlg.editing ? 'Edit Staff' : 'Add Staff'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitStaff} className="space-y-3">
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                {staffPhoto ? <img src={staffPhoto} alt="preview" className="w-full h-full object-cover" /> :
                  staffDlg.editing?.photo_url ? <img src={staffDlg.editing.photo_url} alt="current" className="w-full h-full object-cover" /> :
                  <Camera className="w-8 h-8 text-slate-300" />}
              </div>
              <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-100 hover:bg-slate-200 text-sm">
                <Camera className="w-4 h-4" /> Upload Photo
                <input type="file" accept="image/*" onChange={(e) => handlePhotoChange(e, setStaffPhoto)} className="hidden" data-testid="staff-photo-input" />
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Name *</Label><Input value={staffForm.name} onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })} required data-testid="staff-name" /></div>
              <div><Label>Designation</Label><Input value={staffForm.designation} onChange={(e) => setStaffForm({ ...staffForm, designation: e.target.value })} placeholder="e.g., Warden, Cook, Cleaner" data-testid="staff-designation" /></div>
              <div><Label>Contact</Label><Input value={staffForm.contact} onChange={(e) => setStaffForm({ ...staffForm, contact: e.target.value })} placeholder="10-digit mobile" /></div>
              <div><Label>Date of Birth</Label><Input type="date" value={staffForm.dob} onChange={(e) => setStaffForm({ ...staffForm, dob: e.target.value })} /></div>
              <div><Label>Date of Joining</Label><Input type="date" value={staffForm.doj} onChange={(e) => setStaffForm({ ...staffForm, doj: e.target.value })} data-testid="staff-doj" /></div>
              <div><Label>Aadhaar No</Label><Input value={staffForm.aadhaar} onChange={(e) => setStaffForm({ ...staffForm, aadhaar: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Address</Label><Input value={staffForm.address} onChange={(e) => setStaffForm({ ...staffForm, address: e.target.value })} /></div>
              <div><Label>Monthly Salary (Rs.)</Label><Input type="number" min="0" step="1" value={staffForm.salary} onChange={(e) => setStaffForm({ ...staffForm, salary: e.target.value })} data-testid="staff-salary" /></div>
              <div><Label>Status</Label>
                <Select value={staffForm.status} onValueChange={(v) => setStaffForm({ ...staffForm, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="resigned">Resigned</SelectItem>
                    <SelectItem value="terminated">Terminated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Bank Name</Label><Input value={staffForm.bank_name} onChange={(e) => setStaffForm({ ...staffForm, bank_name: e.target.value })} /></div>
              <div><Label>Account No.</Label><Input value={staffForm.bank_account} onChange={(e) => setStaffForm({ ...staffForm, bank_account: e.target.value })} /></div>
              <div><Label>IFSC Code</Label><Input value={staffForm.bank_ifsc} onChange={(e) => setStaffForm({ ...staffForm, bank_ifsc: e.target.value.toUpperCase() })} /></div>
              <div className="md:col-span-2"><Label>Remarks</Label><Textarea rows={2} value={staffForm.remarks} onChange={(e) => setStaffForm({ ...staffForm, remarks: e.target.value })} /></div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button type="button" variant="outline" onClick={() => setStaffDlg({ open: false, editing: null })}>Cancel</Button>
              <Button type="submit" className="bg-indigo-700 hover:bg-indigo-800 text-white" data-testid="save-staff-btn">
                {staffDlg.editing ? 'Update Staff' : 'Add Staff'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={delInmate.open}
        onOpenChange={(o) => setDelInmate({ open: o, item: o ? delInmate.item : null })}
        onConfirm={doDeleteInmate}
        title="Delete Inmate"
        description="This will permanently remove the inmate record."
        itemName={delInmate.item?.name || ''}
      />
      <DeleteConfirmDialog
        open={delStaff.open}
        onOpenChange={(o) => setDelStaff({ open: o, item: o ? delStaff.item : null })}
        onConfirm={doDeleteStaff}
        title="Delete Staff"
        description="This will permanently remove the staff record."
        itemName={delStaff.item?.name || ''}
      />
    </div>
  );
};

export default CentreDetail;
